import memoize from 'lodash-es/memoize';
import { WIKI_CATEGORIES } from 'mk/autogenerated/wikiCategories';
import { removeQuotes } from 'mk2/helpers/article_ast/utils';
import { getLogger } from 'mk2/logger';
import React from 'react';

const logger = getLogger('wiki/helpers.tsx');

export const UNLISTED_WIKI_CATEGORIES = [11, 12, 13, 14]; // 'Ine', 'FAQ', 'Centrum recenzii', 'Kocikopedia '
export const WIKI_REVIEWS_ARTICLE_SLUG = WIKI_CATEGORIES.filter((wc) => wc.id === 13)[0].slug;
export const WIKI_OTHERS_ARTICLE_SLUG = WIKI_CATEGORIES.filter((wc) => wc.id === 11)[0].slug;
export const WIKI_CALCULATORS_ARTICLE_SLUG = WIKI_CATEGORIES.filter((wc) => wc.id === 15)[0].slug;

export const WIKI_EXPERIENCE_MENU_MORE = (id: number) => `wiki-experience-menu-more-${id}`;
export const isFaq = (categorySlug: string) => categorySlug === 'faq';
export const isNoAdsCategory = (categorySlug: string) => isFaq(categorySlug) || categorySlug === WIKI_OTHERS_ARTICLE_SLUG;
export const isArticleOfReviewsCategory = (categorySlug: string) => categorySlug === WIKI_REVIEWS_ARTICLE_SLUG;
export const isReviewHomepageArticle = (categorySlug: string, articleSlug: string) =>
    categorySlug === WIKI_REVIEWS_ARTICLE_SLUG &&
    articleSlug === WIKI_REVIEWS_ARTICLE_SLUG;

export const getPhotoVersionFactory = memoize((versionsWithFallbacks) => {
    return (photo) => {
        for (const version of versionsWithFallbacks) {
            if (photo.versions.includes(version)) {
                return version;
            }
        }
        return versionsWithFallbacks[versionsWithFallbacks.length - 1];
    };
});


const RE_VIMEO_CODE = /(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+).*/i;
const RE_YOUTUBE_URL = /((?:https?:\/\/)?(?:www\.)?(?:youtube(?:-nocookie)?\.com|youtu\.?be)\/(?:[^\s",]+))/i;
const RE_YOUTUBE_CODE = /^.*(?:youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^\s?&]{11}).*$/i;
const RE_INSTAGRAM_URL = /((?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:reel|p)\/.*)/i;
const RE_TIKTOK_URL = /(?:https?:\/\/)?(?:m\.|www\.|vm\.)?tiktok\.com\/?.*/i;
const RE_TIKTOK_VIDEO_URL = /(?:https?:\/\/)?(?:www\.|vm\.)?tiktok\.com\/(?:@[\w.-]*\/video\/\d+([\/?].*)?)/i;
const RE_TIKTOK_SHORTENED_URL = /(?:https?:\/\/)?(?:www\.|vm\.)?tiktok\.com\/(?:[a-zA-Z0-9]+\/?)/i;


export function extractFromUrl(url: string) {
    let youtubeCode: string | null = null;
    let instagramPostCode: string | null = null;
    let instagramPostUrl: string | null = null;
    let tiktokPostUrl: string | null = null;
    let vimeoCode: string | null = null;

    if (!url) return { youtubeCode, instagramPostCode, instagramPostUrl, tiktokPostUrl, vimeoCode };

    // YouTube
    const ytUrlMatch = url.match(RE_YOUTUBE_URL);
    if (ytUrlMatch) {
        const ytCodeMatch = ytUrlMatch[1].match(RE_YOUTUBE_CODE);
        if (ytCodeMatch) youtubeCode = ytCodeMatch[1];
    }

    // Vimeo
    const vimeoMatch = url.match(RE_VIMEO_CODE);
    if (vimeoMatch) vimeoCode = vimeoMatch[1];

    // Instagram
    const instaMatch = url.match(RE_INSTAGRAM_URL);
    if (instaMatch) {
        instagramPostUrl = instaMatch[1];
        // Optionally extract code from URL (last part after /p/ or /reel/)
        const parts = instagramPostUrl.split('/');
        instagramPostCode = parts.filter(Boolean).pop() || null;
    }

    // TikTok
    const tiktokMatch = url.match(RE_TIKTOK_VIDEO_URL) || url.match(RE_TIKTOK_SHORTENED_URL) || url.match(RE_TIKTOK_URL);
    if (tiktokMatch) tiktokPostUrl = tiktokMatch[0];

    return { youtubeCode, instagramPostCode, instagramPostUrl, tiktokPostUrl, vimeoCode };
}

export function extractFromSnippet(snippetJson) {
    let youtubeCode;
    let instagramPostCode;
    let instagramPostUrl;
    let tiktokPostUrl;
    let vimeoCode;

    try {
        const parsed = JSON.parse(snippetJson);
        // Expecting: array → object with type "youtube" → elements → attribute_value
        for (const block of parsed) {
            if (block.type === 'youtube' && Array.isArray(block.elements)) {
                for (const el of block.elements) {
                    if (el.type === 'attribute' && Array.isArray(el.elements)) {
                        const name = el.elements.find((x: any) => x.type === 'attribute_name');
                        const value = el.elements.find((x: any) => x.type === 'attribute_value');
                        if (name?.text === 'code' && value?.text) {
                            youtubeCode = removeQuotes(value.text);
                        }
                    }
                }
            }
            if (block.type === 'vimeo' && Array.isArray(block.elements)) {
                for (const el of block.elements) {
                    if (el.type === 'attribute' && Array.isArray(el.elements)) {
                        const name = el.elements.find((x: any) => x.type === 'attribute_name');
                        const value = el.elements.find((x: any) => x.type === 'attribute_value');
                        if (name?.text === 'code' && value?.text) {
                            vimeoCode = removeQuotes(value.text);
                        }
                    }
                }
            }
            if (block.type === 'instagram' && Array.isArray(block.elements)) {
                for (const el of block.elements) {
                    if (el.type === 'attribute' && Array.isArray(el.elements)) {
                        const name = el.elements.find((x: any) => x.type === 'attribute_name');
                        const value = el.elements.find((x: any) => x.type === 'attribute_value');
                        if (name?.text === 'code' && value?.text) {
                            instagramPostCode = removeQuotes(value.text);
                        }
                        if (name?.text === 'url' && value?.text) {
                            instagramPostUrl = removeQuotes(value.text);
                        }
                    }
                }
            }
            if (block.type === 'tiktok' && Array.isArray(block.elements)) {
                for (const el of block.elements) {
                    if (el.type === 'attribute' && Array.isArray(el.elements)) {
                        const name = el.elements.find((x: any) => x.type === 'attribute_name');
                        const value = el.elements.find((x: any) => x.type === 'attribute_value');
                        if (name?.text === 'url' && value?.text) {
                            tiktokPostUrl = removeQuotes(value.text);
                        }
                    }
                }
            }
        }
    } catch (err) {
        logger.error(err);
    }
    return {
        youtubeCode,
        instagramPostCode,
        instagramPostUrl,
        tiktokPostUrl,
        vimeoCode,
    };
}
